// components/media/FolderSidebar.tsx
import { cn } from "@/lib/utils";
import { Folder, FolderOpen, ChevronRight, Home } from "lucide-react";
import { useState } from "react";
import type { MediaFolder } from "@/types/media";

type FolderSidebarProps = {
  tree: MediaFolder[]; // root-level folders (each can have children)
  currentPath?: string;
  onSelect: (folder: MediaFolder | null) => void;
};

export default function FolderSidebar({
  tree,
  currentPath,
  onSelect,
}: FolderSidebarProps) {
  return (
    <div className="w-full">
      <FolderNode
        label="Root"
        path=""
        isActive={!currentPath || currentPath === ""}
        onClick={() => onSelect(null)}
        isRoot={true}
      />
      <div className="mt-1.5 space-y-0.5">
        {tree.map((n) => (
          <RecursiveNode
            key={n.id}
            node={n}
            currentPath={currentPath}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}

function RecursiveNode({
  node,
  currentPath,
  onSelect,
}: {
  node: MediaFolder;
  currentPath?: string;
  onSelect: (folder: MediaFolder | null) => void;
}) {
  const [open, setOpen] = useState(true);
  const hasChildren = !!node.children?.length;
  const depth = node.fileUrl.split("/").length - 1;

  return (
    <div>
      <FolderNode
        label={node.fileName}
        path={node.fileUrl}
        isActive={currentPath === node.fileUrl}
        count={node.assetCount}
        depth={depth}
        isExpandable={hasChildren}
        isOpen={open}
        onClick={() => onSelect(node)}
        onToggle={() => setOpen((o) => !o)}
      />
      {hasChildren && open && (
        <div className="ml-6 mt-0.5 space-y-0.5 border-l border-slate-200 pl-2">
          {node.children!.map((c) => (
            <RecursiveNode
              key={c.fileUrl}
              node={c}
              currentPath={currentPath}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FolderNode({
  label,
  path,
  isActive,
  count,
  depth,
  isExpandable,
  isOpen,
  onClick,
  onToggle,
  isRoot = false,
}: {
  label: string;
  path: string;
  isActive?: boolean;
  count?: number;
  depth?: number;
  isExpandable?: boolean;
  isOpen?: boolean;
  onClick?: () => void;
  onToggle?: () => void;
  isRoot?: boolean;
}) {
  return (
    <div
      className={cn(
        "group flex items-center gap-2 rounded-lg px-2.5 py-2 text-sm cursor-pointer transition-all duration-150",
        "hover:bg-slate-100/80 hover:shadow-sm",
        isActive
          ? "bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 shadow-sm border border-blue-200/50"
          : "text-slate-700"
      )}
      onClick={onClick}
    >
      {isExpandable ? (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggle?.();
          }}
          className={cn(
            "grid h-5 w-5 place-items-center rounded transition-all",
            "hover:bg-slate-200/60 active:scale-95",
            isActive && "hover:bg-blue-200/60"
          )}
          title={isOpen ? "Collapse" : "Expand"}
        >
          <ChevronRight
            className={cn(
              "h-3.5 w-3.5 transition-transform duration-200 text-slate-500",
              isOpen && "rotate-90",
              isActive && "text-blue-600"
            )}
          />
        </button>
      ) : (
        <div className="h-5 w-5 flex items-center justify-center">
          {depth && depth > 0 && <div className="h-3 w-px bg-slate-300" />}
        </div>
      )}

      {isRoot ? (
        <Home
          className={cn(
            "h-4 w-4 flex-shrink-0 transition-colors",
            isActive
              ? "text-blue-600"
              : "text-slate-500 group-hover:text-slate-700"
          )}
        />
      ) : isActive ? (
        <FolderOpen className="h-4 w-4 flex-shrink-0 text-blue-600" />
      ) : (
        <Folder className="h-4 w-4 flex-shrink-0 text-slate-500 group-hover:text-slate-600 transition-colors" />
      )}

      <span
        className={cn(
          "truncate flex-1 font-medium",
          isActive && "font-semibold"
        )}
      >
        {label}
      </span>

      {typeof count === "number" && (
        <span
          className={cn(
            "ml-auto rounded-full px-2 py-0.5 text-[10px] font-medium flex-shrink-0 transition-colors",
            isActive
              ? "bg-blue-100 text-blue-700"
              : "bg-slate-200 text-slate-600 group-hover:bg-slate-300"
          )}
        >
          {count}
        </span>
      )}
    </div>
  );
}
